/**
 * The language Propstack forced on the iframe, for the lifetime of this page load.
 *
 * `?lang` is only present on the URL Propstack opens. PF v2 navigates between steps
 * (`usePfPath` builds `${base}/${step}` and carries no search string), so from the second screen
 * onwards the parameter is simply gone — and with it the whole locale self-heal, silently. Legacy
 * cannot hit this: it is one route driven by internal step state, so its URL never changes.
 *
 * Remembered in a module variable rather than re-attached to every navigation because the parameter
 * is Propstack's to set, not ours to re-assert on a URL they can read. It is deliberately NOT
 * persisted: a reload re-enters through Propstack's URL, which carries `?lang` again, and a stale
 * value surviving into an ordinary session would force a language nobody asked for.
 */

let forcedLocale: string | null = null

/**
 * Records the forced locale the first time the URL carries one, and answers with the locale in
 * force now. A later URL that carries one again wins, so a re-entry can change it.
 *
 * @example
 * rememberEmbedLocale(params.get('lang'))  // 'de' on entry, and still 'de' on /catalogue
 */
export const rememberEmbedLocale = (fromUrl: string | null): string | null => {
  if (fromUrl) forcedLocale = fromUrl
  return forcedLocale
}

/** Drops the remembered locale. Leaving the embed, and the reset a test needs between cases. */
export const forgetEmbedLocale = (): void => {
  forcedLocale = null
}
